import { useCallback, useEffect, useRef, useState } from 'react';
import { waterfall } from 'async';
import axios from 'axios';
import _get from 'lodash/get';
import _isArray from 'lodash/isArray';
import _isEmpty from 'lodash/isEmpty';
import _cloneDeep from 'lodash/cloneDeep';
import _isFunction from 'lodash/isFunction';

import { get, logError, post } from 'utils';
import { CustomToast } from 'components';

const API_METHODS = {
  GET: () => get,
  POST: () => post
};

const DEFAULT = {
  method: 'GET',
  loadOnMount: true,
  initialData: null,
  isPublicAPI: false,
  payload: {},
  routeParam: '',
  routeParams: {},
  axiosConfig: {},
  onSkipRequest: () => false,
  onTransform: data => data,
  onSuccess: () => {},
  onError: () => {},
  ignoreReadModeCheck: false,
  errorMessage: 'Unable to fetch data at the moment. Please try again later.'
};

/**
 * Arguments of useSeriesFetch hook
 * @param {import('utils/types').SeriesApi[]} apis List of the objects defining the apiKey and the request configuration
 * @param {import('utils/types').SeriesFetchConfig} config Optional parameters to control the behaviour of the hook
 */
export default function useSeriesFetch(apis = [], config = {}) {
  if (!_isArray(apis)) {
    throw new Error('"apis" must be an Array in the useSeriesFetch hook');
  }

  const initialData = _get(config, 'initialData', DEFAULT.initialData);
  const loadOnMount = _get(config, 'loadOnMount', DEFAULT.loadOnMount);
  const errorMessage = _get(config, 'errorMessage', DEFAULT.errorMessage);
  const onTransform = _get(config, 'onTransform', DEFAULT.onTransform);
  const onSuccess = _get(config, 'onSuccess', DEFAULT.onSuccess);
  const onError = _get(config, 'onError', DEFAULT.onError);

  const [data, setData] = useState(initialData);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(loadOnMount && !_isEmpty(apis));

  const isMountedRef = useRef(false);
  const apisRef = useRef(apis);
  const onTransformCallbackRef = useRef(onTransform);
  const onSuccessCallbackRef = useRef(onSuccess);
  const onErrorCallbackRef = useRef(onError);
  const abortControllerRef = useRef(null);
  const errorMessageRef = useRef(errorMessage);

  useEffect(() => {
    apisRef.current = apis;
    onTransformCallbackRef.current = onTransform;
    onSuccessCallbackRef.current = onSuccess;
    onErrorCallbackRef.current = onError;
    errorMessageRef.current = errorMessage;
  });

  /**
   * @param {Object} payloadOverrides
   */
  const fetchData = useCallback((payloadOverrides = {}) => {
    const requests = createRequests(
      apisRef.current,
      payloadOverrides,
      abortControllerRef
    );

    if (_isEmpty(requests)) return;

    setIsError(false);
    setIsLoading(true);
    waterfall(requests, (error, data) => {
      if (axios.isCancel(error)) return;

      if (error) {
        setIsError(true);
        const errorMsg = _isFunction(errorMessageRef.current)
          ? errorMessageRef.current(error, _get(error, 'response.data.data'))
          : errorMessageRef.current;

        if (errorMsg && !error.notified) {
          CustomToast({ type: 'error', msg: errorMsg });
        }

        logError(error, { customMessage: errorMsg });
        onErrorCallbackRef.current(error, _get(error, 'response.data.data'));
      } else {
        const transformedData = onTransformCallbackRef.current(
          _cloneDeep(data)
        );
        setData(transformedData);
        onSuccessCallbackRef.current(transformedData, data);
      }

      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!isMountedRef.current && loadOnMount) {
      isMountedRef.current = true;
      fetchData();
    }
  }, [fetchData, loadOnMount]);

  useEffect(() => {
    const abortController = abortControllerRef.current;
    return () => {
      if (abortController) {
        abortController.abort();
      }
    };
  }, []);

  return {
    data,
    isError,
    isLoading,
    fetchData
  };
}

// --------------------------------------------------------------------------

/**
 * Arguments of useSeriesFetch hook
 * @param {import('utils/types').SeriesApi[]} apis
 * @param {Object} payloadOverrides
 * @param {{ current: AbortController }} abortControllerRef
 */
function createRequests(
  apis = [],
  payloadOverrides = {},
  abortControllerRef = {}
) {
  let isFirst = true;

  if (abortControllerRef.current) {
    abortControllerRef.current.abort();
  }

  abortControllerRef.current = new AbortController();

  return apis.reduce((acc, { apiKey, dataKey, ...meta } = {}) => {
    if (!apiKey || !dataKey) {
      throw new Error(
        'apiKey and the dataKey are required in the useSeriesFetch hook'
      );
    }

    const apiVerb = _get(meta, 'method', DEFAULT.method);
    const payload = _get(meta, 'payload', DEFAULT.payload);
    const axiosConfig = _get(meta, 'axiosConfig', DEFAULT.axiosConfig);
    const routeParam = _get(meta, 'routeParam', DEFAULT.routeParam);
    const routeParams = _get(meta, 'routeParams', DEFAULT.routeParams);
    const isPublicAPI = _get(meta, 'isPublicAPI', DEFAULT.isPublicAPI);
    const onTransform = _get(meta, 'onTransform', DEFAULT.onTransform);
    const onSkipRequest = _get(meta, 'onSkipRequest', DEFAULT.onSkipRequest);
    const ignoreReadModeCheck = _get(
      meta,
      'ignoreReadModeCheck',
      DEFAULT.ignoreReadModeCheck
    );

    const methodGen = _get(API_METHODS, apiVerb, API_METHODS.GET);
    const apiMethod = methodGen();
    const overridePayload = _get(payloadOverrides, dataKey, {});

    const apiCall = (
      payload,
      successCallback = () => {},
      errorCallback = () => {}
    ) => {
      apiMethod(
        { apiKey, noTokenRequired: isPublicAPI, ignoreReadModeCheck },
        {
          params: {
            routeParam,
            routeParams,
            ...payload,
            ...overridePayload
          },
          config: {
            ...axiosConfig,
            signal: abortControllerRef.current.signal
          }
        }
      )
        .then(({ data }) => {
          successCallback({ [dataKey]: onTransform(data) });
        })
        .catch(err => errorCallback(err));
    };

    if (isFirst) {
      isFirst = false;
      acc.push(function(callback) {
        apiCall(
          payload,
          data => callback(null, data),
          err => callback(err)
        );
      });
    } else {
      acc.push(function(aggregateData, callback) {
        const skip = onSkipRequest(aggregateData);
        if (skip) {
          return callback(null, aggregateData);
        }
        const payloadData = _isFunction(payload)
          ? payload(aggregateData)
          : payload;
        apiCall(
          payloadData,
          data => callback(null, { ...aggregateData, ...data }),
          err => callback(err)
        );
      });
    }

    return acc;
  }, []);
}
